import React, { useEffect, useMemo, useState } from "react";
import { FaBuilding, FaMapMarkerAlt, FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import TheaterListLayout from "../components/theaters/TheaterListLayout";
import ModernLoader from "../components/common/ModernLoader";
import api from "../services/api";
import "../styles/theaters-page.css";

const Theaters = () => {
  const navigate = useNavigate();

  const [theaters, setTheaters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCity, setSelectedCity] = useState("");

  useEffect(() => {
    fetchTheaters();

    // Real-time update listeners
    const handleUpdate = (event) => {
      const isSync = !!event?.detail;
      console.log(`🔄 Real-time update detected on Theaters (Sync: ${isSync}): Re-fetching...`);
      fetchTheaters(isSync);
    };

    window.addEventListener("theaters-updated", handleUpdate);
    window.addEventListener("theater-added", handleUpdate);
    window.addEventListener("theater-updated", handleUpdate);
    window.addEventListener("theater-deleted", handleUpdate);
    window.addEventListener("shows-updated", handleUpdate);
    window.addEventListener("movie-updated", handleUpdate);
    window.addEventListener("movies-updated", handleUpdate);

    return () => {
      window.removeEventListener("theaters-updated", handleUpdate);
      window.removeEventListener("theater-added", handleUpdate);
      window.removeEventListener("theater-updated", handleUpdate);
      window.removeEventListener("theater-deleted", handleUpdate);
      window.removeEventListener("shows-updated", handleUpdate);
      window.removeEventListener("movie-updated", handleUpdate);
      window.removeEventListener("movies-updated", handleUpdate);
    };
  }, []);

  useEffect(() => {
    const applyStoredLocation = () => {
      const storedLocation = localStorage.getItem("userLocation");
      if (!storedLocation) return;

      try {
        const locationData = JSON.parse(storedLocation);
        if (locationData?.city) {
          setSelectedCity(locationData.city);
        }
      } catch (error) {
        console.error("Failed to parse stored location", error);
      }
    };

    applyStoredLocation();
    window.addEventListener("storage", applyStoredLocation);
    window.addEventListener("locationUpdated", applyStoredLocation);

    return () => {
      window.removeEventListener("storage", applyStoredLocation);
      window.removeEventListener("locationUpdated", applyStoredLocation);
    };
  }, []);

  const fetchTheaters = async (isSync = false) => {
    try {
      if (!isSync) setLoading(true);
      const response = await api.get("/theaters/with-shows");
      const theatersData = Array.isArray(response?.data)
        ? response.data
        : response?.data?.theaters || [];

      setTheaters(theatersData);
    } catch (error) {
      console.error("Fetch theaters error:", error);
      toast.error("Failed to fetch theaters");
    } finally {
      setLoading(false);
    }
  };

  const cityOptions = useMemo(() => {
    const uniqueCities = new Set();

    theaters.forEach((theater) => {
      const cityName = theater?.address?.city || theater?.city;
      if (!cityName) return;

      uniqueCities.add(cityName);
    });

    return Array.from(uniqueCities).sort((left, right) =>
      left.localeCompare(right),
    );
  }, [theaters]);

  const filteredTheaters = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return theaters
      .filter((theater) => theater?.status?.isActive !== false)
      .filter((theater) => {
        if (!selectedCity) return true;

        const cityName = (
          theater?.address?.city ||
          theater?.city ||
          ""
        ).toLowerCase();
        return cityName === selectedCity.toLowerCase();
      })
      .filter((theater) => {
        if (!query) return true;

        const searchText = [
          theater?.name,
          theater?.location,
          theater?.address?.area,
          theater?.address?.city,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchText.includes(query);
      });
  }, [theaters, selectedCity, searchTerm]);

  const availableShowsCount = useMemo(
    () =>
      filteredTheaters.reduce((total, theater) => {
        const movieShows = Array.isArray(theater?.movies)
          ? theater.movies.reduce(
            (movieTotal, movie) =>
              movieTotal +
              (Array.isArray(movie?.shows) ? movie.shows.length : 0),
            0,
          )
          : 0;

        return total + movieShows;
      }, 0),
    [filteredTheaters],
  );

  const handleMovieClick = (movieId, theater) => {
    if (!movieId) return;

    const cityName = theater?.address?.city || theater?.city || selectedCity;
    if (cityName) {
      localStorage.setItem("selectedTheaterCity", cityName);
    }

    navigate(
      `/movie/${movieId}/showtimes?city=${encodeURIComponent(cityName || "")}&theaterId=${theater?._id || ""}`,
    );
  };

  const handleShowClick = (showId) => {
    if (!showId) return;
    navigate(`/booking/${showId}`);
  };

  if (loading) {
    return (
      <div className="district-theaters-loader-wrap">
        <ModernLoader text="Loading Theaters" />
      </div>
    );
  }

  return (
    <div className="district-theaters-page">
      <section className="district-theaters-shell">
        <header className="district-theaters-hero">
          <h1>Premium Theaters</h1>
          <p>
            Find cinema venues, explore showtimes, and book by show ID in one
            flow.
          </p>

          <div className="district-theaters-stats">
            <div className="district-theaters-stat-item">
              <strong>{filteredTheaters.length}</strong>
              <span>Theaters</span>
            </div>
            <div className="district-theaters-stat-item">
              <strong>{cityOptions.length}</strong>
              <span>Cities</span>
            </div>
            <div className="district-theaters-stat-item">
              <strong>{availableShowsCount}</strong>
              <span>Showtimes</span>
            </div>
          </div>
        </header>

        <section className="district-theaters-filter-card">
          <div className="district-theaters-search-wrap">
            <FaSearch className="district-theaters-search-icon" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by theater name or location"
              aria-label="Search theaters"
            />
          </div>

          <div className="district-theaters-city-wrap">
            <label htmlFor="district-city-select">
              <FaMapMarkerAlt />
              City
            </label>
            <select
              id="district-city-select"
              value={selectedCity}
              onChange={(event) => setSelectedCity(event.target.value)}
            >
              <option value="">All Cities</option>
              {cityOptions.map((cityName) => (
                <option key={cityName} value={cityName}>
                  {cityName}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className="district-theaters-clear-btn"
            onClick={() => {
              setSearchTerm("");
              setSelectedCity("");
            }}
            disabled={!searchTerm && !selectedCity}
          >
            Clear
          </button>
        </section>

        <section className="district-theaters-result-head">
          <div>
            <h2>
              <FaBuilding />
              {filteredTheaters.length} Theater
              {filteredTheaters.length === 1 ? "" : "s"}
              {selectedCity ? ` in ${selectedCity}` : ""}
            </h2>
            <p>
              Showtime tiles include the unique show ID for quick booking
              lookup.
            </p>
          </div>
        </section>

        {filteredTheaters.length > 0 ? (
          <TheaterListLayout
            theaters={filteredTheaters}
            onMovieClick={handleMovieClick}
            onShowClick={handleShowClick}
          />
        ) : (
          <div className="district-theaters-empty-state">
            <FaBuilding />
            <h3>No theaters found</h3>
            <p>
              Try another city or clear your search to view all available
              theaters.
            </p>
            <button
              type="button"
              onClick={() => {
                setSearchTerm("");
                setSelectedCity("");
              }}
            >
              Reset Filters
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default Theaters;
